/* eslint-disable no-param-reassign */
/**
 * This function processes html content by using replace
 * operations.
 *
 * @param {string} htmlContent HTML string to be processed.
 *
 * @returns {string} Processed HTML string.
 * */
export const processHtmlContent = (htmlContent) => {
  let result = htmlContent;
  if (result) {
    // Fix broken image by replacing src with smallest data URI image possible for a transparent image
    // See: https://stackoverflow.com/questions/6018611/smallest-data-uri-image-possible-for-a-transparent-image
    result = result.replace(
      /<img/g,
      `<img onerror="this.src='data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=='"`
    );
    // Remove external links
    // See: https://stackoverflow.com/questions/20867719/removing-a-href-tag-using-regex
    result = result.replace(/<a[^>]*>(.*?)<\/a>/g, "$1");
    // Convert h1 tags to h2 tags
    result = result.replace(/<h1(.*?)h1>/g, "<h2$1h2>");
  }
  return result;
};

/**
 * Recursively flattens `array`.
 *
 * @param {array} arr Array to be processed.
 *
 * @returns {array} Flattened array.
 * */
export const flattenDeep = (arr) =>
  Array.isArray(arr)
    ? arr.reduce((a, b) => a.concat(flattenDeep(b)), [])
    : [arr];

/**
 * Checks if `value` is an empty object, array or string.
 *
 * @param {any} value Value to check.
 *
 * @returns {boolean} Boolean value - if value is empty or not.
 * */
export const isEmpty = (value) =>
  value === undefined ||
  value === null ||
  (typeof value === "object" && Object.keys(value).length === 0) ||
  (typeof value === "string" && value.length === 0);

/**
 * Returns application for showing interview preparation.
 *
 * @param {array} applications Array of applications to check.
 *
 * @param {array} stages Application stages to check.
 *
 * @returns {object} Selected application.
 * */
export const selectApplicationToShowInterviewPreparation = (
  applications,
  stages = [
    "SCHEDULED_FOR_INTERVIEW",
    "TOBE_SCHEDULED_FOR_INTERVIEW",
    "CANDIDATE_APPLIED"
  ]
) => {
  if (applications) {
    if (applications.length > 0) {
      // Get only paid jobs
      applications = applications.filter((application) => {
        if (application) {
          const { job } = application;
          const jobInterviewContent = job && job.interview_content;
          const latestQuestionRound = job && job.latest_question_round;
          const orgInterviewContent =
            job && job.organization && job.organization.interview_content;
          const interviewTrainingContent =
            job &&
            job.functional_area &&
            job.functional_area.interview_training_content;
          const pricingPlanType = job && job.pricing_plan_type || '';

          const hasInterviewContent =
            jobInterviewContent ||
            orgInterviewContent ||
            latestQuestionRound > 0 ||
            (interviewTrainingContent && interviewTrainingContent.length > 0);

          return (
            hasInterviewContent &&
            ["PREPAID", "POSTPAID", "TEMP_STAFFING"].includes(pricingPlanType)
          );
        }
        return false;
      });

      const sfiApplications = stages.includes("SCHEDULED_FOR_INTERVIEW")
        ? applications
            .filter((application) => {
              const today = new Date();
              const hasFutureInterview =
                application.interview &&
                new Date(application.interview.date) - today > 0;

              return (
                application.stage === "SCHEDULED_FOR_INTERVIEW" &&
                hasFutureInterview
              );
            })
            .sort(
              (x, y) => new Date(y.interview.date) - new Date(x.interview.date)
            )
        : [];
      const tsfiApplications = stages.includes("TOBE_SCHEDULED_FOR_INTERVIEW")
        ? applications.filter(
            (application) =>
              application.stage === "TOBE_SCHEDULED_FOR_INTERVIEW"
          )
        : [];
      const capApplications = stages.includes("CANDIDATE_APPLIED")
        ? applications.filter(
            (application) => application.stage === "CANDIDATE_APPLIED"
          )
        : [];

      if (sfiApplications.length > 0) return sfiApplications[0];
      if (tsfiApplications.length > 0) return tsfiApplications[0];
      if (capApplications.length > 0) return capApplications[0];
    } else {
      return null;
    }
  }
  return null;
};

export const areArraysSame = (arr1, arr2) => {
  if (arr1.length !== arr2.length) return false;
  let obj1 = {},
    obj2 = {};
  for (let i = 0; i < arr1.length; i++) {
    let key = arr1[i];
    obj1[key] = (obj1[key] || 0) + 1;
  }
  for (let j = 0; j < arr2.length; j++) {
    let key = arr2[j];
    obj2[key] = (obj2[key] || 0) + 1;
  }
  for (let key in obj1) {
    if (obj1[key] !== obj2[key]) {
      return false;
    }
  }

  return true;
};
